import { usePageModels } from '@/context/PageModels';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { DEFAULT_ELASTIC_FIELDS } from '@/features/shared/Hooks/elasticSearch/useElasticSearch.constants';
import useElasticOnDemand from '@/features/shared/Hooks/useElasticOnDemand';
import useIsMobile from '@/features/shared/Hooks/useIsMobile';
import { MuiCircularProgress, MuiTab, MuiTabs } from '@/features/shared/material-ui';
import PortalCarousel from '@/features/shared/portal/PortalCarousel';
import TabPanel from '@/features/shared/portal/TabbedListContent/components/TabPanel';
import useTabbedListContentStyles from '@/features/shared/portal/TabbedListContent/TabbedListContent.styles';
import { IS_PREVIEW, LoadingStatusValues } from '@/utils/constants';
import resolveTaxonomyFacets from '@/utils/resolvers/resolveTaxonomyFacets';
import { BrPageContext } from '@bloomreach/react-sdk';
import clsx from 'clsx';
import { first, get, pipe, reduce, split } from 'lodash/fp';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { parseEsTabbedContentCardResults } from './EsTabbedContent.helpers';
import { EsTabbedContentProps, IEsTabbedContent } from './EsTabbedContent.interfaces';
import EsTabbedContentPlaceholder from './EsTabbedContent.placeholder';
import useEsTabbedContentStyles from './EsTabbedContent.styles';

const getFacetFromKey = pipe(split('/'), first);
const DEFAULT_ES_TABBED_CONFIG = {
  size: '5',
  sortBy: 'most-recent',
};

const parseSorByToElastic = (sortBy: string) => {
  if (/a-z/.test(sortBy)) return 'title-asc';
  if (/z-a/.test(sortBy)) return 'title-desc';
  return sortBy;
};

const EsTabbedContent = ({
  tabContent,
  setDynamicFields,
  fetchElasticSearchData,
  isLoading = false,
  className,
}: EsTabbedContentProps & {
  setDynamicFields: any;
  fetchElasticSearchData: any;
  isLoading: boolean;
}) => {
  const classes = useEsTabbedContentStyles();
  const tabbedListClasses = useTabbedListContentStyles();
  const [cardContentTab, setTabCardContentTab] = useState<CardType[]>([]);
  const [tabValue, setTabValue] = useState<number>(0);
  const isMobile = useIsMobile();
  const firstTabRendered = useRef(false);
  const page = useContext(BrPageContext);

  const { results = [], status } = useContext(ElasticComponentContext);

  useEffect(() => {
    if (results.length < 1) return;
    setTabCardContentTab(parseEsTabbedContentCardResults(results, page));
  }, [results, fetchElasticSearchData, page]);

  const handleChangeTab = useCallback(
    (_: any, newValue: number) => {
      setTabValue(newValue);
      const currentTabContent = get(newValue, tabContent);
      const { size, sortBy, keys } = currentTabContent;
      setDynamicFields({ size, sortBy, keys });
      fetchElasticSearchData(true);
    },
    [fetchElasticSearchData, setDynamicFields, tabContent],
  );

  useEffect(() => {
    if (tabContent.length > 0 && !firstTabRendered.current) {
      handleChangeTab(null, 0);
      firstTabRendered.current = true;
    }
  }, [tabContent, handleChangeTab]);

  const content =
    status === LoadingStatusValues.loading ? (
      <div className="loader-wrapper">
        <MuiCircularProgress size="24px" color="secondary" />
      </div>
    ) : (
      <div className="tabs-content">
        {tabContent.map((tab: IEsTabbedContent, index) => (
          <TabPanel value={tabValue} index={index} key={index}>
            {!isLoading && !!results.length && (
              <PortalCarousel cards={cardContentTab} key={index} />
            )}
          </TabPanel>
        ))}
      </div>
    );

  return (
    <div className={clsx('es-tabbed-content', tabbedListClasses.root, classes.root, className)}>
      <div className="tabs-header">
        <MuiTabs
          value={tabValue}
          onChange={handleChangeTab}
          variant="scrollable"
          scrollButtons={isMobile ? 'on' : 'off'}
          className="tabbed-list-content-tabs"
        >
          {tabContent.map((tab: IEsTabbedContent, index) => (
            <MuiTab label={tab.tabTitle} className={tabbedListClasses.tab} key={index} />
          ))}
        </MuiTabs>
      </div>
      {IS_PREVIEW ? <EsTabbedContentPlaceholder /> : content}
    </div>
  );
};

const withElasticSearch = (Component: any) => (props: EsTabbedContentProps) => {
  const { tabContent } = props;
  const pageModels = usePageModels();
  const [dynamicFields, setDynamicFields] = useState<any>({});

  const facets = useMemo(
    () =>
      pipe(
        reduce((acc, tab: IEsTabbedContent) => {
          const topics = tab.keys.map(getFacetFromKey) as string[];
          return [...acc, ...topics];
        }, [] as string[]),
        (chars) => [...new Set(chars)],
        (rawVal) => resolveTaxonomyFacets({ rawVal }),
      )(tabContent),
    [tabContent],
  );

  const { es, status, sortOptions, search, fetchElasticSearchData } = useElasticOnDemand({
    pageModels,
    isElasticAllowed: true,
    facetsList: facets || [],
    keys: dynamicFields.keys,
    sortBy: parseSorByToElastic(dynamicFields.sortBy || DEFAULT_ES_TABBED_CONFIG.sortBy),
    size: dynamicFields.size || DEFAULT_ES_TABBED_CONFIG.size,
    fields: DEFAULT_ELASTIC_FIELDS,
  });

  return (
    <ElasticComponentContext.Provider
      value={{
        queryParams: search,
        sortOptions,
        status,
        updateQueryParams: () => {},
        pagination: es.response.pagination,
        facets: es.facets,
        results: es.response.results,
        requestID: es.response?.meta?.request_id,
      }}
    >
      <Component
        {...props}
        setDynamicFields={setDynamicFields}
        fetchElasticSearchData={fetchElasticSearchData}
        isLoading={es.isLoading}
      />
    </ElasticComponentContext.Provider>
  );
};

export default withElasticSearch(EsTabbedContent);
